<script setup>
import { useThemeStore } from "./stores/theme";
import { DEFAULT_LAYOUT, SETTINGS_LAYOUT } from "@/constants/layouts.js";
import FullPageLoader from "./components/ui-kit/base/FullPageLoader.vue";
import { adjustColorByDifference } from "@/utils/theme";
import { useAppInitializer } from "@/composables/useAppInitializer";
import { useMessengerStore } from "./stores/messenger";
import { useWfbChatWidgetStore } from "./stores/wfbChatWidget";
import { useWfbChatStore } from "./stores/wfbChat";

const { isLoading, initializeData } = useAppInitializer();

const setHeightVH = () => {
  const doc = document.documentElement;
  doc.style.setProperty("--vh", window.innerHeight * 0.01 + "px");
};

const mapLayouts = {
  [DEFAULT_LAYOUT]: defineAsyncComponent(
    () => import("@/layouts/DefaultLayout.vue")
  ),
  [SETTINGS_LAYOUT]: defineAsyncComponent(
    () => import("@/layouts/SettingsLayout.vue")
  ),
};

const theme = useThemeStore();
const messenger = useMessengerStore();
const wfbChatWidgetStore = useWfbChatWidgetStore();
const wfbChatStore = useWfbChatStore();

theme.updateKioskMode();

const route = useRoute();
const layout = computed(() => {
  return mapLayouts[route.meta.layout];
});


watch(
  () => theme.primaryColor,
  (baseColor) => {
    const shades = {
      0: adjustColorByDifference(
        baseColor,
        0.9607843137254902,
        0.984313725490196,
        1
      ),
      50: adjustColorByDifference(
        baseColor,
        0.8901960784313725,
        0.9411764705882353,
        0.984313725490196
      ),
      100: adjustColorByDifference(
        baseColor,
        0.7725490196078432,
        0.8823529411764706,
        0.9725490196078431
      ),
      150: adjustColorByDifference(
        baseColor,
        0.6509803921568628,
        0.8274509803921568,
        0.9568627450980393
      ),
      200: adjustColorByDifference(
        baseColor,
        0.5176470588235295,
        0.7686274509803922,
        0.9411764705882353
      ),
      250: baseColor,
      300: adjustColorByDifference(
        baseColor,
        0,
        0.49411764705882355,
        0.6549019607843137
      ),
      350: adjustColorByDifference(
        baseColor,
        0,
        0.396078431372549,
        0.611764705882353
      ),
      400: adjustColorByDifference(
        baseColor,
        0,
        0.2823529411764706,
        0.4588235294117647
      ),
      450: adjustColorByDifference(
        baseColor,
        0.10588235294117647,
        0.21568627450980393,
        0.2823529411764706
      ),
    };

    for (const [key, value] of Object.entries(shades)) {
      document.documentElement.style.setProperty(`--primary-${key}`, value);
    }
  },
  { immediate: true }
);

onBeforeMount(() => {
  setHeightVH();
});

onMounted(() => {
  initializeData()

  window.addEventListener("resize", setHeightVH);
  setHeightVH();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", setHeightVH);
  // clean up websocket connection
  if (messenger.websocket) {
    messenger.closeConnection();
  }
  wfbChatWidgetStore.cleanup();
  wfbChatStore.cleanup();
});
</script>

<template>
  <div v-if="isLoading">
    <FullPageLoader :show="true" />
  </div>
  <component v-else-if="layout" :is="layout">
    <router-view />
  </component>
  <RouterView v-else />
</template>

<style scoped></style>
